/**
 * Family Reunion Mixer — everyone attending a reunion sees a
 * personalised card for every other person showing how they're related.
 *
 * Route: /family/:treeId/mixer
 *
 * UX:
 *   1. Pick "I am ..." (dropdown or search of tree members).
 *   2. See a grid of cards for every other person in the tree +
 *      linked families with an instant relationship label:
 *         "You are Mary's second cousin (through the George Family)."
 *   3. Tap any card → jump to that person's canonical profile.
 *
 * The heavy lifting happens on the backend (`shortest_path` +
 * `describe_relationship`); this page is a thin client.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { http, resolveImage } from "@/lib/api";
import { ArrowLeft, Users, Loader2, Search, Sparkles, PartyPopper } from "lucide-react";

export default function ReunionMixer() {
  const { treeId } = useParams();
  const [params, setParams] = useSearchParams();
  const me = params.get("me") || "";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    setLoading(true);
    const qs = me ? `?me=${encodeURIComponent(me)}` : "";
    http.get(`/family-trees/${treeId}/mixer${qs}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [treeId, me]);

  const filtered = useMemo(() => {
    if (!data?.attendees) return [];
    const term = q.trim().toLowerCase();
    if (!term) return data.attendees;
    return data.attendees.filter((a) =>
      [a.first_name, a.last_name, a.nickname, a.sp_id]
        .filter(Boolean).join(" ").toLowerCase().includes(term),
    );
  }, [data, q]);

  const pickMe = (spId) => {
    const next = new URLSearchParams(params);
    if (spId) next.set("me", spId); else next.delete("me");
    setParams(next, { replace: true });
  };

  if (loading) return <div className="py-24 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  if (!data) return (
    <div className="max-w-xl mx-auto py-16 text-center">
      <p className="opacity-70">This family tree isn't available.</p>
      <Link to="/family" className="wofy-btn-primary mt-4">Back to family trees</Link>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto px-4 lg:px-6 py-6" data-testid="reunion-mixer">
      <Link to={`/family/${treeId}/manage`} className="text-sm flex items-center gap-1 opacity-70 hover:opacity-100 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to Management Center
      </Link>

      {/* Hero */}
      <div
        className="p-6 rounded-2xl border flex items-center gap-4 flex-wrap"
        style={{
          borderColor: "#DB277733",
          background: "linear-gradient(135deg, #FDF2F8 0%, #EFF6FF 100%)",
        }}
      >
        <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: "linear-gradient(135deg,#DB2777,#7C3AED)", color: "white" }}>
          <PartyPopper className="w-7 h-7" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xs font-mono uppercase tracking-widest opacity-60">Reunion Mixer</div>
          <h1 className="font-display text-3xl leading-tight" data-testid="mixer-title">
            Who's who at the <span style={{ color: "#DB2777" }}>{data.tree?.name}</span> reunion
          </h1>
          <p className="text-sm opacity-80 mt-1">
            {data.attendee_count} {data.attendee_count === 1 ? "person" : "people"} in this family
            {data.related_trees_included?.length ? ` + ${data.related_trees_included.length} marriage-linked ${data.related_trees_included.length === 1 ? "family" : "families"}` : ""}.
          </p>
        </div>
      </div>

      {/* Me picker */}
      <div className="mt-5 wofy-card">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-4 h-4" style={{ color: "#DB2777" }} />
          <h3 className="font-display text-lg">First, tell us who <span style={{ color: "#DB2777" }}>you</span> are</h3>
        </div>
        <p className="text-xs opacity-70 mb-3">
          Then we'll instantly show your relationship to every other person here — no more awkward introductions.
        </p>
        <select
          value={me}
          onChange={(e) => pickMe(e.target.value)}
          className="w-full px-3 py-2 rounded-lg border text-sm"
          style={{ borderColor: "var(--wofy-border)" }}
          data-testid="mixer-me-picker"
        >
          <option value="">— Pick your name —</option>
          {data.attendees.map((a) => (
            <option key={a.sp_id} value={a.sp_id}>
              {[a.first_name, a.last_name].filter(Boolean).join(" ") || a.sp_id}
              {a.birth_date ? ` · b. ${a.birth_date.slice(0, 4)}` : ""}
            </option>
          ))}
        </select>
      </div>

      {/* Search */}
      <div className="mt-5">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 opacity-60" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter attendees by name…"
            className="w-full pl-9 pr-3 py-2 rounded-lg border text-sm"
            style={{ borderColor: "var(--wofy-border)" }}
            data-testid="mixer-search"
          />
        </div>
      </div>

      {/* Attendee cards */}
      <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="mixer-grid">
        {filtered.length === 0 && (
          <div className="col-span-full wofy-card text-center py-10 opacity-70">
            No attendees match "{q}".
          </div>
        )}
        {filtered.map((a) => {
          const rel = data.relationships?.[a.sp_id];
          const isMe = me === a.sp_id;
          const fullName = [a.first_name, a.last_name].filter(Boolean).join(" ") || "Unnamed";
          return (
            <Link
              key={a.sp_id}
              to={`/person/${a.sp_id}`}
              className="p-4 rounded-xl border transition hover:shadow-md hover:-translate-y-0.5"
              style={{
                borderColor: isMe ? "#DB2777" : "var(--wofy-border)",
                background: isMe ? "#FDF2F8" : "white",
              }}
              data-testid={`mixer-card-${a.sp_id}`}
            >
              <div className="flex items-center gap-3">
                {a.photo
                  ? <img src={resolveImage(a.photo)} alt="" className="w-12 h-12 rounded-full object-cover" />
                  : <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: "#DB27771A", color: "#DB2777" }}>
                      <Users className="w-6 h-6" />
                    </div>}
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm truncate">{fullName}</div>
                  <div className="text-[10px] font-mono opacity-60 truncate">{a.sp_id}</div>
                </div>
              </div>

              {/* Relationship label — the magic. */}
              {me && rel && (
                <div
                  className="mt-3 p-2.5 rounded-lg text-sm"
                  style={{
                    background: isMe ? "#DB277711" : (rel.found ? "#EEF2FF" : "#F3F4F6"),
                    color: isMe ? "#DB2777" : (rel.found ? "#4338CA" : "#6B7280"),
                  }}
                  data-testid={`mixer-rel-${a.sp_id}`}
                >
                  {isMe ? (
                    <span className="font-semibold">This is you — welcome! 🎉</span>
                  ) : rel.found ? (
                    <>
                      <span className="opacity-80">You are </span>
                      <span className="font-semibold">{fullName}'s {rel.description}</span>
                      {rel.distance != null && <span className="opacity-60"> · {rel.distance} step{rel.distance === 1 ? "" : "s"}</span>}
                    </>
                  ) : (
                    <span className="opacity-70">Relationship not yet linked in the tree</span>
                  )}
                </div>
              )}

              {/* Which families this person belongs to */}
              {(a.member_of || []).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {a.member_of.slice(0, 3).map((t) => (
                    <span
                      key={t.id}
                      className="text-[10px] px-1.5 py-0.5 rounded font-mono"
                      style={{ background: "#EEF2FF", color: "#4338CA" }}
                    >
                      {t.fam_id || t.name}
                    </span>
                  ))}
                </div>
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
